import React, { useState, useMemo, useEffect } from "react";
import _ from "lodash";
import cx from "classnames";
import { MappingInfoIcon, MappingWarningIcon } from "./MappingIcons";
import ColumnHeaderMappingWarning from "../ColumnHeaderMappingWarning";
import VirtualizedDataTable from "./VirtualizedDataTable";
import { Fields } from "./Fields";
import SubmissionErrorRowToggle from "../SubmissionErrorRowToggle";
import SubmissionDataFormatSelector from "../SubmissionDataFormatSelector";
import SubmissionEntitySelector from "../SubmissionEntitySelector";
import SubmissionFileSelector from "../SubmissionFileSelector";
import FieldInspector from "../FieldInspector";
import AmgenNotice, { AMGEN, showAmgenWarning } from "./AmgenNotice";
import {
  buildManufacturerStats,
  buildColumnMappingWarnings,
  buildProcessedData,
  buildMfrNdcs,
  buildManufacturerFilteredData,
  buildManufacturerRowCounts,
  buildEntityOptions,
  buildEntityFilteredData,
} from "./dataOutputUtils";

const SUBMISSION_TYPE_CONFIG = {
  medical: {
    fields: Fields["medical"],
    skipColumns: ["formatted_claim_number"],
    ndcField: "ndc_11",
    entityField: "id_340b_raw",
  },
  pharmacy: {
    fields: Fields["pharmacy"],
    skipColumns: ["formatted_rx_number"],
    ndcField: "ndc",
    entityField: "contracted_entity_id",
  },
  pharmacy_encounter: {
    fields: Fields["pharmacy_encounter"],
    skipColumns: ["formatted_rx_number"],
    ndcField: "ndc",
    entityField: "contracted_entity_id",
  },
  medical_encounter: {
    fields: Fields["medical_encounter"],
    skipColumns: ["formatted_claim_number"],
    ndcField: "ndc",
    entityField: "id_340b_raw",
  },
};

const ManufacturerButtonSelect = ({
  manufacturerStats,
  selectedManufacturer,
  totalRows,
  onSelect,
  flagAmgen = false,
}) => (
  <div className="flex flex-wrap gap--8 mb--16">
    <button
      className={cx(
        "button button--lg",
        { "button--outline button--white": selectedManufacturer.name },
        { "button--blue": !selectedManufacturer.name }
      )}
      onClick={() => onSelect({ name: null, has_errors: false })}
    >
      All {`(${totalRows})`}
    </button>
    {Object.entries(manufacturerStats).map(([mfr, mfrCounts]) => (
      <button
        key={mfr}
        className={cx(
          "button button--lg",
          { "button--outline button--white": selectedManufacturer.name !== mfr },
          { "button--blue": selectedManufacturer.name === mfr }
        )}
        style={{ position: "relative" }}
        onClick={() => onSelect({ name: mfr, has_errors: mfrCounts.hasErrors })}
      >
        {`${mfr} (${mfrCounts.rows})`}
        {mfrCounts.hasErrors && <MappingWarningIcon />}
        {flagAmgen && mfr === AMGEN && (
          <MappingInfoIcon light={selectedManufacturer.name === AMGEN} />
        )}
      </button>
    ))}
  </div>
);

const DataOutputProcessed = ({
  submissions,
  ndcList,
  requiredShape,
  submissionType,
  viewFileRequest,
}) => {
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [showErrorRows, setShowErrorRows] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState({
    name: null,
    has_errors: false,
  });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [inspectedCellData, setInspectedCellData] = useState(null);
  const [inspectorContainer, setInspectorContainer] = useState(null);

  const { fields, skipColumns, ndcField, entityField } = SUBMISSION_TYPE_CONFIG[submissionType];
  const attachment = submissions.find((s) => s.fileId === selectedFileId) ?? null;

  // Data flows through a chain of filters, each memoised on the previous stage:
  //
  //   submissions ──► data ──► manufacturerFilteredData ──► entityFilteredData ──► filteredData ──► table
  //   (by file)      (mfr)                (entity)            (data view: all / errors only)
  //
  //   1. data                    — rows for the selected file, or all files (buildProcessedData)
  //   2. manufacturerFilteredData — narrowed to the selected manufacturer's NDCs
  //   3. entityFilteredData       — narrowed to the selected 340B entity
  //   4. filteredData             — narrowed to error rows when the "error rows" view is active
  //
  //   entityOptions is submission-level: it reacts only to the manufacturer
  //   selection, not to the file or entity filters above.
  //
  //   The manufacturer button counts (manufacturerStats/totalRows) run a parallel
  //   branch — data ──► entityFilteredForCounts ──► categoryFilteredForCounts —
  //   which applies the file/entity/error-toggle filters but skips the manufacturer
  //   filter, so each button's count reflects the currently displayed category
  //   (alert vs. rows-for-submission) rather than every row ever matched.

  const data = useMemo(
    () => buildProcessedData(submissions, attachment),
    [attachment?.processedData, submissions]
  );

  const columnMappingWarnings = useMemo(
    () => buildColumnMappingWarnings(attachment ? [attachment] : submissions),
    [attachment, submissions]
  );

  // Row counts on the manufacturer buttons should reflect only the rows in the
  // currently displayed category (alert vs. rows-for-submission), independent of
  // which manufacturer is selected — so they're computed before the manufacturer
  // filter is applied, but after the file/entity/error-toggle filters.
  const entityFilteredForCounts = useMemo(
    () => buildEntityFilteredData(data, selectedEntity, entityField),
    [data, selectedEntity, entityField]
  );

  const categoryFilteredForCounts = useMemo(
    () => entityFilteredForCounts.filter((row) => (showErrorRows ? row.hasErrors : !row.hasErrors)),
    [entityFilteredForCounts, showErrorRows]
  );

  const manufacturerRowCounts = useMemo(
    () => buildManufacturerRowCounts(categoryFilteredForCounts),
    [categoryFilteredForCounts]
  );

  const manufacturerStats = useMemo(() => {
    const mappingWarnings = buildManufacturerStats(submissions);
    const stats = {};
    for (const mfr of Object.keys(mappingWarnings)) {
      stats[mfr] = {
        rows: manufacturerRowCounts[mfr] || 0,
        hasErrors: mappingWarnings[mfr].hasErrors,
      };
    }
    return stats;
  }, [submissions, manufacturerRowCounts]);

  const mfrNdcs = useMemo(
    () => buildMfrNdcs(ndcList, selectedManufacturer.name),
    [ndcList, selectedManufacturer.name]
  );

  const manufacturerFilteredData = useMemo(
    () => buildManufacturerFilteredData(data, selectedManufacturer.name, mfrNdcs, ndcField),
    [data, selectedManufacturer.name, mfrNdcs, ndcField]
  );

  const entityOptions = useMemo(() => buildEntityOptions(submissions), [submissions]);

  const entityFilteredData = useMemo(
    () => buildEntityFilteredData(manufacturerFilteredData, selectedEntity, entityField),
    [manufacturerFilteredData, selectedEntity, entityField]
  );

  const filteredData = showErrorRows
    ? entityFilteredData.filter((row) => row.hasErrors)
    : entityFilteredData.filter((row) => !row.hasErrors);

  const visibleColumns = requiredShape.filter((col) => !skipColumns.includes(col));

  const clearInspector = () => {
    setSelectedCell(null);
    setInspectedCellData(null);
  };

  const handleSetShowErrorRows = (value) => {
    setShowErrorRows(value);
    clearInspector();
  };

  const handleSelectFile = (fileId) => {
    setSelectedFileId(fileId);
    clearInspector();
  };

  // Sync the file selector to summary tab and DataInput attachment removal
  useEffect(() => {
    if (viewFileRequest?.fileId) {
      const filenames = submissions.map((s) => s.fileId);

      // do nothing if removed file was not selected
      if (viewFileRequest.isDeletion && filenames.includes(selectedFileId)) {
        return;
      } else {
        handleSelectFile(viewFileRequest.fileId);
      }
    }
  }, [viewFileRequest]);

  // clear selection on attachment changes to avoid stale selection state after rows update.
  useEffect(() => {
    clearInspector();
  }, [submissions]);

  const handleSelectManufacturer = (mfr) => {
    setSelectedManufacturer(mfr);
    clearInspector();
  };

  const handleSelectEntity = (entity) => {
    setSelectedEntity(entity);
    clearInspector();
  };

  const handleInspectColumn = (columnName, cellData) => {
    setSelectedCell(`header-${columnName}`);
    setInspectedCellData(cellData);
  };

  const columnDefs = useMemo(
    () =>
      visibleColumns.map((columnName, index) => ({
        id: columnName,
        headerClassName: cx("data-table__head__column", "data-table__head__column--hoverable", {
          "data-table__head__column--inspected": selectedCell === `header-${columnName}`,
        }),
        header: (
          <ColumnHeaderMappingWarning
            columnName={columnName}
            columnLabel={fields[columnName]?.label ?? columnName}
            description={fields[columnName]?.description}
            warningFilenames={columnMappingWarnings[columnName]}
            lastColumn={index === visibleColumns.length - 1}
            inspect={(cellData) => handleInspectColumn(columnName, cellData)}
          />
        ),
        cell: (row, rowIndex) => {
          const cellError = _.find(row.validations, { field: columnName });
          const hasMappingWarning = columnMappingWarnings[columnName];
          const cellValue = showRaw ? (row.rawData?.[columnName] ?? "") : row.data[columnName];
          const cellKey = `${rowIndex}-${columnName}`;
          const cellClassName = cx("data-table__row__item", "data-table__row__item--hoverable", {
            "data-table__row__item--invalid": cellError,
            "data-table__row__item--inspected": selectedCell === cellKey,
          });

          return (
            <td
              className={cellClassName}
              style={{ position: "relative" }}
              onClick={() => {
                setSelectedCell(cellKey);
                setInspectedCellData({
                  field: fields[columnName]?.label ?? columnName,
                  value: row.data[columnName],
                  originalValue: row.rawData?.[columnName] ?? "",
                  message: cellError?.message,
                  filename: row.filename,
                  lineNumber: row.line_number,
                });
              }}
            >
              {cellValue}
            </td>
          );
        },
      })),
    [visibleColumns, columnMappingWarnings, showRaw, selectedCell, inspectedCellData, fields]
  );

  const totalRows = categoryFilteredForCounts.length;
  const showAmgenWarningIcon = showAmgenWarning(submissionType, submissions);

  return (
    <div ref={setInspectorContainer}>
      {inspectedCellData && (
        <FieldInspector
          cellData={inspectedCellData}
          onClose={clearInspector}
          inspectorContainer={inspectorContainer}
        />
      )}
      <div className="t--upcase t--sm t--subtitle t--500 mb--8">Manufacturers</div>
      <ManufacturerButtonSelect
        manufacturerStats={manufacturerStats}
        selectedManufacturer={selectedManufacturer}
        totalRows={totalRows}
        onSelect={handleSelectManufacturer}
        flagAmgen={showAmgenWarningIcon}
      />
      <div className="line-divider mt--24 mb--24"></div>
      {selectedManufacturer?.name === AMGEN && showAmgenWarningIcon && (
        <AmgenNotice submissionType={submissionType} />
      )}
      <div className="flex gap--8">
        <div className="flex flex-column">
          <div className="t--upcase t--sm t--subtitle mb--8 t--500">View As</div>
          <div className="flex gap--8">
            <SubmissionDataFormatSelector showRaw={showRaw} setShowRaw={setShowRaw} />
            <div className="submission-selector__container__divider"></div>
          </div>
        </div>

        <div className="flex flex-column justify-content-between flex-fill">
          <div className="t--upcase t--sm t--subtitle mb--8 t--500">Filters</div>
          <div className="submission-selector__container mb--16">
            <div className="flex gap--16 ml--auto">
              <SubmissionEntitySelector
                entityIds={entityOptions}
                selectedEntity={selectedEntity}
                setSelectedEntity={handleSelectEntity}
              />
              <SubmissionFileSelector
                submissions={submissions}
                selectedFile={selectedFileId}
                setSelectedFile={handleSelectFile}
              />
              <SubmissionErrorRowToggle
                showErrorRows={showErrorRows}
                setShowErrorRows={handleSetShowErrorRows}
              />
            </div>
          </div>
        </div>
      </div>
      <VirtualizedDataTable
        data={_.compact(filteredData)}
        columns={columnDefs}
        getRowClassName={() => "data-table__row"}
        minRows={15}
      />
    </div>
  );
};

export default DataOutputProcessed;
